import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Customer, CreateCustomerForm } from '../../types'
import { useCreateCustomer, useUpdateCustomer } from '../../hooks/api/useCustomers'
import { useAreas } from '../../hooks/api/useAreas'
import { RootState } from '../../store'

interface CustomerFormProps {
  open: boolean
  onClose: () => void
  customer?: Customer | null
  mode: 'create' | 'edit'
}

const CustomerForm = ({ open, onClose, customer, mode }: CustomerFormProps) => {
  const [stiErrors, setStiErrors] = useState({ strn: false, ntn: false })
  const [formData, setFormData] = useState<CreateCustomerForm>({
    area_id: '',
    name: '',
    cnic: '',
    phone: '',
    address: '',
    is_filer: false,
    discount: 0,
    customer_type: 'retail',
    is_sales_tax_customer: false,
    strn: '',
    ntn: '',
  })

  // Get current user to check role
  const { user } = useSelector((state: RootState) => state.auth)

  // Check if user can edit discount and filer fields
  const canEditRestrictedFields = user?.role === 'suadmin'
  const stiFeatureEnabled = user?.company_features?.sales_tax_invoice ?? false
  const cartonDiscountPricingEnabled = user?.company_features?.carton_discount_pricing === true
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false

  const createCustomerMutation = useCreateCustomer()
  const updateCustomerMutation = useUpdateCustomer()
  const { data: areasData } = useAreas(0, 500, true)

  const areas = (areasData?.results || []).filter((area) => area.is_active !== false)
  const currentArchivedArea = mode === 'edit' && customer && typeof customer.area_id !== 'string' && customer.area_id?.is_active === false
    ? customer.area_id
    : null
  const selectedArea = areas.find((a) => a._id === formData.area_id) || null

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && customer) {
        setFormData({
          area_id: typeof customer.area_id === 'string' ? customer.area_id : customer.area_id?._id || '',
          name: customer.name || '',
          cnic: customer.cnic || '',
          phone: customer.phone || '',
          address: customer.address || '',
          is_filer: customer.is_filer || false,
          discount: customer.discount || 0,
          customer_type: customer.customer_type || 'retail',
          is_sales_tax_customer: customer.is_sales_tax_customer || false,
          strn: customer.strn || '',
          ntn: customer.ntn || '',
        })
      } else {
        setFormData({
          area_id: '',
          name: '',
          cnic: '',
          phone: '',
          address: '',
          is_filer: false,
          discount: 0,
          customer_type: 'retail',
          is_sales_tax_customer: false,
          strn: '',
          ntn: '',
        })
      }
    }
  }, [mode, customer, open])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        area_id: '',
        name: '',
        cnic: '',
        phone: '',
        address: '',
        is_filer: false,
        discount: 0,
        customer_type: 'retail',
        is_sales_tax_customer: false,
        strn: '',
        ntn: '',
      })
    }
  }, [open])

  const handleChange = (field: keyof CreateCustomerForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCnicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 13)

    let formattedValue = ''
    if (digits.length <= 5) {
      formattedValue = digits
    } else if (digits.length <= 12) {
      formattedValue = `${digits.slice(0, 5)}-${digits.slice(5)}`
    } else {
      formattedValue = `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
    }

    setFormData(prev => ({
      ...prev,
      cnic: formattedValue,
    }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (formData.is_sales_tax_customer) {
      const errors = {
        strn: !formData.strn?.trim(),
        ntn: !formData.ntn?.trim(),
      }
      if (errors.strn || errors.ntn) {
        setStiErrors(errors)
        return
      }
    }
    setStiErrors({ strn: false, ntn: false })

    try {
      if (mode === 'create') {
        await createCustomerMutation.mutateAsync(formData)
      } else if (customer) {
        await updateCustomerMutation.mutateAsync({
          id: customer._id,
          data: formData,
        })
      }
      onClose()
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  }

  const isLoading = createCustomerMutation.isPending || updateCustomerMutation.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Customer' : 'Edit Customer'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel>Area</InputLabel>
                <Select
                  value={formData.area_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, area_id: e.target.value }))}
                  label="Area"
                >
                  {currentArchivedArea && (
                    <MenuItem value={currentArchivedArea._id} disabled>
                      {currentArchivedArea.area_name} (Disabled — select an active area)
                    </MenuItem>
                  )}
                  {areas.map((area) => (
                    <MenuItem key={area._id} value={area._id}>
                      {area.area_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
                disabled={isLoading}
              />
            </Grid>
            {selectedArea && (
              <Grid item xs={12}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Booker</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {typeof selectedArea.booker_id === 'object'
                      ? selectedArea.booker_id.name
                      : selectedArea.booker_id}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CNIC"
                value={formData.cnic}
                onChange={handleCnicChange}
                disabled={isLoading}
                placeholder="48219-7350462-8"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NTN"
                value={formData.ntn || ''}
                onChange={(e) => {
                  handleChange('ntn')(e as React.ChangeEvent<HTMLInputElement>)
                  if (e.target.value.trim()) setStiErrors(prev => ({ ...prev, ntn: false }))
                }}
                disabled={isLoading}
                required={formData.is_sales_tax_customer}
                error={stiErrors.ntn}
                placeholder="e.g. 4174941-3"
                helperText={
                  stiErrors.ntn
                    ? 'NTN is required for STI customers'
                    : undefined
                }
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                disabled={isLoading}
                type="tel"
                placeholder="optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleChange('address')}
                multiline
                rows={2}
                disabled={isLoading}
                placeholder="optional"
              />
            </Grid>
            {cartonDiscountPricingEnabled ? (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isLoading}>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    value={formData.customer_type}
                    label="Customer Type"
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      customer_type: e.target.value as CreateCustomerForm['customer_type'],
                    }))}
                  >
                    <MenuItem value="retail">Retail</MenuItem>
                    <MenuItem value="wholesale">Wholesale</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  value={formData.discount}
                  onChange={handleChange('discount')}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  disabled={isLoading || !canEditRestrictedFields}
                  helperText={!canEditRestrictedFields ? "Only BOSS can edit discount" : "Enter discount percentage (e.g 5)"}
                />
              </Grid>
            )}
            {advanceTaxEnabled && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mt: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_filer}
                      onChange={handleChange('is_filer')}
                      disabled={isLoading || !canEditRestrictedFields}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Filer Customer
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {!canEditRestrictedFields
                          ? "Only BOSS can change filer status"
                          : "Check if this customer is a tax filer"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
            )}

            {/* Sales Tax Invoice section — suadmin + STI feature enabled only */}
            {canEditRestrictedFields && stiFeatureEnabled && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_sales_tax_customer || false}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              is_sales_tax_customer: e.target.checked,
                            }))
                          }
                          disabled={isLoading}
                          color="warning"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Sales Tax Invoice Customer
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable to issue Sales Tax Invoices (STI) for this customer
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>

                {formData.is_sales_tax_customer && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="STRN"
                      value={formData.strn || ''}
                      onChange={(e) => {
                        handleChange('strn')(e as React.ChangeEvent<HTMLInputElement>)
                        if (e.target.value.trim()) setStiErrors(prev => ({ ...prev, strn: false }))
                      }}
                      disabled={isLoading}
                      required
                      error={stiErrors.strn}
                      placeholder="e.g. 2356842536"
                      helperText={
                        stiErrors.strn
                          ? 'STRN is required for STI customers'
                          : '10-digit Sales Tax Registration Number'
                      }
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : mode === 'create' ? 'Create Customer' : 'Update Customer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CustomerForm
